import {openDB} from 'idb';
import {isServerMessage} from '../serverMessages';
import {localClientId, saveOutboxMessage} from './chatOutbox';

const DATABASE_NAME = 'cos-chat-cache';
const DATABASE_VERSION = 1;
const MESSAGE_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_MESSAGE_CONVERSATIONS = 200;
const MAX_MESSAGES_PER_CONVERSATION = 50;

let databasePromise;

function cacheSupported() {
    return typeof indexedDB !== 'undefined' && typeof IDBKeyRange !== 'undefined';
}

function database() {
    if (!cacheSupported()) return Promise.resolve(null);
    if (!databasePromise) {
        databasePromise = openDB(DATABASE_NAME, DATABASE_VERSION, {
            upgrade(db) {
                const meta = db.createObjectStore('meta', {keyPath: 'scope'});
                meta.createIndex('by-updated', 'updated_at');

                const conversations = db.createObjectStore('conversations', {keyPath: 'cache_key'});
                conversations.createIndex('by-scope-updated', ['scope', 'updated_at']);
                conversations.createIndex('by-scope-agent', ['scope', 'agent_key']);

                const messages = db.createObjectStore('messages', {keyPath: 'cache_key'});
                messages.createIndex('by-scope-conversation-sequence', ['scope', 'conversation_id', 'sequence']);
                messages.createIndex('by-scope-cached', ['scope', 'cached_at']);

                const groups = db.createObjectStore('agent_groups', {keyPath: 'cache_key'});
                groups.createIndex('by-scope-label', ['scope', 'label']);
            },
            blocked() {
                databasePromise = undefined;
            },
            terminated() {
                databasePromise = undefined;
            },
        }).catch(() => {
            databasePromise = undefined;
            return null;
        });
    }
    return databasePromise;
}

export function chatCacheScope(identity) {
    const normalizedIdentity = String(identity || '').trim().toLowerCase();
    if (!normalizedIdentity) return '';
    const origin = typeof window === 'undefined' ? 'server' : window.location.origin;
    return `${origin}:${normalizedIdentity}`;
}

function conversationKey(scope, id) {
    return `${scope}:conversation:${id}`;
}

function messageKey(scope, conversationId, id) {
    return `${scope}:message:${conversationId}:${id}`;
}

function groupKey(scope, key) {
    return `${scope}:group:${key}`;
}

function sanitizeConversation(scope, conversation) {
    return {
        ...conversation,
        cache_key: conversationKey(scope, conversation.id),
        scope,
        agent_key: conversation.auto_channel?.agent_group?.key || '',
        updated_at: conversation.updated_at || conversation.created_at || new Date(0).toISOString(),
        cached_at: new Date().toISOString(),
    };
}

function stripConversationCacheFields(record) {
    if (!record) return null;
    const {cache_key, scope, agent_key, cached_at, ...conversation} = record;
    return conversation;
}

function sanitizeMessage(scope, conversationId, message) {
    const sanitizeAttachment = ({download_url, local_file, ...attachment}) => attachment;
    return {
        ...message,
        attachments: (message.attachments || []).map(sanitizeAttachment),
        quoted_message: message.quoted_message ? {
            ...message.quoted_message,
            attachments: (message.quoted_message.attachments || []).map(sanitizeAttachment),
        } : null,
        cache_key: messageKey(scope, conversationId, message.id),
        scope,
        conversation_id: conversationId,
        cached_at: new Date().toISOString(),
    };
}

function stripMessageCacheFields(record) {
    const {cache_key, scope, cached_at, ...message} = record;
    return message;
}

export async function readCachedChat(scope, {selectedId, limit = 50} = {}) {
    const db = await database();
    if (!db || !scope) return {meta: null, conversations: [], selected: null};
    try {
        const transaction = db.transaction(['meta', 'conversations'], 'readonly');
        const metaPromise = transaction.objectStore('meta').get(scope);
        const index = transaction.objectStore('conversations').index('by-scope-updated');
        const range = IDBKeyRange.bound([scope, ''], [scope, '\uffff']);
        const conversations = [];
        let cursor = await index.openCursor(range, 'prev');
        while (cursor && conversations.length < limit) {
            conversations.push(stripConversationCacheFields(cursor.value));
            cursor = await cursor.continue();
        }
        const selectedRecord = selectedId
            ? await transaction.objectStore('conversations').get(conversationKey(scope, selectedId))
            : null;
        const meta = await metaPromise;
        await transaction.done;
        return {meta: meta || null, conversations, selected: stripConversationCacheFields(selectedRecord)};
    } catch {
        return {meta: null, conversations: [], selected: null};
    }
}

export async function writeBootstrapCache(scope, payload) {
    const db = await database();
    if (!db || !scope) return;
    const transaction = db.transaction(['meta', 'conversations'], 'readwrite');
    const conversationStore = transaction.objectStore('conversations');
    for (const conversation of payload.conversations || []) {
        await conversationStore.put(sanitizeConversation(scope, conversation));
    }
    if (payload.selected_conversation) {
        await conversationStore.put(sanitizeConversation(scope, payload.selected_conversation));
    }
    const previous = await transaction.objectStore('meta').get(scope);
    await transaction.objectStore('meta').put({
        ...previous,
        scope,
        current_user: payload.current_user || previous?.current_user || null,
        event_cursor: Math.max(
            Number(previous?.event_cursor || 0),
            Number(payload.sync_cursor || 0),
        ),
        has_unread: payload.unread_status === 'unavailable' ? previous?.has_unread : Boolean(payload.has_unread),
        unread_count: payload.unread_status === 'unavailable' ? previous?.unread_count : payload.unread_count,
        unread_status: payload.unread_status || 'ready',
        next_cursor: payload.next_cursor || '',
        has_more: Boolean(payload.has_more),
        updated_at: new Date().toISOString(),
    });
    await transaction.done;
}

export async function writeConversationCache(scope, conversations) {
    const db = await database();
    if (!db || !scope || !conversations?.length) return;
    const transaction = db.transaction('conversations', 'readwrite');
    for (const conversation of conversations) {
        await transaction.store.put(sanitizeConversation(scope, conversation));
    }
    await transaction.done;
}

export async function deleteConversationCache(scope, conversationIds) {
    const db = await database();
    if (!db || !scope || !conversationIds?.length) return;
    const transaction = db.transaction(['conversations', 'messages'], 'readwrite');
    for (const id of conversationIds) {
        await transaction.objectStore('conversations').delete(conversationKey(scope, id));
        const index = transaction.objectStore('messages').index('by-scope-conversation-sequence');
        let cursor = await index.openCursor(IDBKeyRange.bound([scope, id, 0], [scope, id, Number.MAX_SAFE_INTEGER]));
        while (cursor) {
            if (isServerMessage(cursor.value) || !localClientId(cursor.value)) await cursor.delete();
            cursor = await cursor.continue();
        }
    }
    await transaction.done;
}

export async function readCachedMessages(scope, conversationId) {
    const db = await database();
    if (!db || !scope || !conversationId) return [];
    try {
        const records = await db.getAllFromIndex(
            'messages',
            'by-scope-conversation-sequence',
            IDBKeyRange.bound([scope, conversationId, 0], [scope, conversationId, Number.MAX_SAFE_INTEGER]),
        );
        const cutoff = Date.now() - MESSAGE_RETENTION_MS;
        return records
            .filter(isServerMessage)
            .filter(record => new Date(record.cached_at).getTime() >= cutoff)
            .sort((left, right) => left.sequence - right.sequence)
            .slice(-MAX_MESSAGES_PER_CONVERSATION)
            .map(stripMessageCacheFields);
    } catch {
        return [];
    }
}

export async function migrateLegacyOutbox(scope, conversationId) {
    const db = await database();
    if (!db || !scope || !conversationId) return;
    const records = await db.getAllFromIndex('messages', 'by-scope-conversation-sequence',
        IDBKeyRange.bound([scope, conversationId, 0], [scope, conversationId, Number.MAX_SAFE_INTEGER]));
    for (const record of records) {
        if (isServerMessage(record) || !localClientId(record)) continue;
        await saveOutboxMessage(scope, record, {ifAbsent: true});
        await db.delete('messages', record.cache_key);
    }
}

export async function writeMessageCache(scope, conversationId, messages) {
    const db = await database();
    if (!db || !scope || !conversationId) return;
    const incoming = (messages || []).filter(isServerMessage).slice(-MAX_MESSAGES_PER_CONVERSATION);
    const transaction = db.transaction('messages', 'readwrite');
    const index = transaction.store.index('by-scope-conversation-sequence');
    let cursor = await index.openCursor(IDBKeyRange.bound([scope, conversationId, 0], [scope, conversationId, Number.MAX_SAFE_INTEGER]));
    while (cursor) {
        // Unmigrated failed content must survive a cache refresh or storage error.
        if (isServerMessage(cursor.value) || !localClientId(cursor.value)) await cursor.delete();
        cursor = await cursor.continue();
    }
    for (const message of incoming) {
        await transaction.store.put(sanitizeMessage(scope, conversationId, message));
    }
    await transaction.done;
    scheduleCachePrune(scope);
}

export async function readCachedGroups(scope) {
    const db = await database();
    if (!db || !scope) return [];
    try {
        const records = await db.getAllFromIndex(
            'agent_groups',
            'by-scope-label',
            IDBKeyRange.bound([scope, ''], [scope, '\uffff']),
        );
        return records.map(({cache_key, scope: ignored, cached_at, ...group}) => group);
    } catch {
        return [];
    }
}

export async function writeCachedGroups(scope, groups) {
    const db = await database();
    if (!db || !scope) return;
    const transaction = db.transaction('agent_groups', 'readwrite');
    const index = transaction.store.index('by-scope-label');
    let cursor = await index.openCursor(IDBKeyRange.bound([scope, ''], [scope, '\uffff']));
    while (cursor) {
        await cursor.delete();
        cursor = await cursor.continue();
    }
    for (const group of groups || []) {
        await transaction.store.put({
            ...group,
            cache_key: groupKey(scope, group.key),
            scope,
            cached_at: new Date().toISOString(),
        });
    }
    await transaction.done;
}

export async function readEventCursor(scope) {
    const db = await database();
    if (!db || !scope) return 0;
    return Number((await db.get('meta', scope))?.event_cursor || 0);
}

export async function writeEventCursor(scope, eventCursor) {
    const db = await database();
    if (!db || !scope) return;
    const transaction = db.transaction('meta', 'readwrite');
    const previous = await transaction.store.get(scope);
    await transaction.store.put({
        ...previous,
        scope,
        event_cursor: Math.max(
            Number(previous?.event_cursor || 0),
            Number(eventCursor) || 0,
        ),
        updated_at: new Date().toISOString(),
    });
    await transaction.done;
}

export async function replaceEventCursor(scope, eventCursor) {
    const db = await database();
    if (!db || !scope) return;
    const transaction = db.transaction('meta', 'readwrite');
    const previous = await transaction.store.get(scope);
    await transaction.store.put({
        ...previous,
        scope,
        event_cursor: Math.max(0, Number(eventCursor) || 0),
        updated_at: new Date().toISOString(),
    });
    await transaction.done;
}

export async function clearChatCacheScope(scope) {
    const db = await database();
    if (!db || !scope) return;
    const transaction = db.transaction(['meta', 'conversations', 'messages', 'agent_groups'], 'readwrite');
    await transaction.objectStore('meta').delete(scope);
    for (const [storeName, indexName] of [
        ['conversations', 'by-scope-updated'],
        ['messages', 'by-scope-cached'],
        ['agent_groups', 'by-scope-label'],
    ]) {
        const index = transaction.objectStore(storeName).index(indexName);
        const range = IDBKeyRange.bound([scope, ''], [scope, '\uffff']);
        let cursor = await index.openCursor(range);
        while (cursor) {
            if (storeName !== 'messages' || isServerMessage(cursor.value) || !localClientId(cursor.value)) await cursor.delete();
            cursor = await cursor.continue();
        }
    }
    await transaction.done;
}

function scheduleCachePrune(scope) {
    const run = () => pruneMessageCache(scope).catch(() => undefined);
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        window.requestIdleCallback(run, {timeout: 2000});
    } else {
        window.setTimeout(run, 0);
    }
}

async function pruneMessageCache(scope) {
    const db = await database();
    if (!db) return;
    const records = await db.getAllFromIndex(
        'messages',
        'by-scope-cached',
        IDBKeyRange.bound([scope, ''], [scope, '\uffff']),
    );
    const cutoff = Date.now() - MESSAGE_RETENTION_MS;
    const latestByConversation = new Map();
    for (const record of records) {
        const cachedAt = new Date(record.cached_at).getTime();
        latestByConversation.set(record.conversation_id, Math.max(latestByConversation.get(record.conversation_id) || 0, cachedAt));
    }
    const retained = new Set([...latestByConversation.entries()]
        .filter(([, cachedAt]) => cachedAt >= cutoff)
        .sort((left, right) => right[1] - left[1])
        .slice(0, MAX_MESSAGE_CONVERSATIONS)
        .map(([conversationId]) => conversationId));
    const transaction = db.transaction('messages', 'readwrite');
    for (const record of records) {
        if (!isServerMessage(record) && localClientId(record)) continue;
        if (!retained.has(record.conversation_id) || new Date(record.cached_at).getTime() < cutoff) {
            await transaction.store.delete(record.cache_key);
        }
    }
    await transaction.done;
}
